import { BigNumber } from 'bignumber.js';
import {
  isAdditionalScanCode as isAdditionalScanCodeUtil,
  isSoldByWeight as isSoldByWeightUtil,
  isSoldByAverageWeight as isSoldByAverageWeightUtil,
  isSoldByRandomWeight as isSoldByRandomWeightUtil,
  hasWeightAbbreviation as hasWeightAbbreviationUtil,
  averageWeight,
  isWeightAndQuantityCustomizable as isWeightAndQuantityCustomizableUtil,
} from '@/utils/item-utils';
import ItemSummary from '@/models/entities/items/item-summary';

export default function useQuantityDisplay() {
  /**
   * Check if an item is sold by average weight.
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isSoldByAverageWeight = (item: ItemSummary): boolean => {
    return isSoldByAverageWeightUtil(item);
  };

  /**
   * Get the "each" count of an item sold by average weight given the quantity.
   * @param {Object} item The item.
   * @param {BigNumber} quantity The quantity of `item`.
   * @return {BigNumber}
   */
  const getEachCountFromQuantity = (item: ItemSummary, quantity: BigNumber): BigNumber => {
    if (!isSoldByAverageWeight(item)) {
      throw new Error('Item count is not defined for items not sold by average weight');
    }
    return quantity.dividedBy(averageWeight(item)).integerValue(BigNumber.ROUND_HALF_UP);
  };

  /**
   * Get the quantity of an item or the average weight if sold by average weight.
   * @param {Object} item The item.
   * @param {BigNumber} quantity The quantity of `item`.
   * @return {BigNumber}
   */
  const getOrderedQuantity = (item: ItemSummary, quantity: BigNumber): BigNumber => {
    if (isSoldByAverageWeight(item)) {
      return averageWeight(item);
    }
    return quantity;
  };

  /**
   * Get weightedItemQuantity of an item. For random weight items, this is simply the weightedItemQuantity.
   * For average weight items, this is derived from calculating the "each".
   * @param {Object} item The item.
   * @param {BigNumber} quantity The quantity of `item`.
   * @param {BigNumber} weightedItemQuantity The weightedItemQuantity of `item`.
   * @return {BigNumber}
   */
  const getWeightedItemQuantity = (
    item: ItemSummary,
    quantity: BigNumber,
    weightedItemQuantity: BigNumber,
  ): BigNumber => {
    if (isSoldByAverageWeight(item)) {
      return getEachCountFromQuantity(item, quantity);
    }
    return weightedItemQuantity;
  };

  /**
   * Get the weight profile abbreviation for an item.
   * For average weight items, is '{abbrv} / ea avg'.
   * For random weight items, this is '{abbrv} / ea'.
   * For items with no weightedItemQuantity, this is {abbrv}.
   * @param {Object} item The item.
   * @param {BigNumber} quantity The quantity of `item`.
   * @param {BigNumber} weightedItemQuantity The weightedItemQuantity of `item`.
   * @return {String}
   */
  const getItemAbbreviation = (
    item: ItemSummary,
    quantity: BigNumber,
    weightedItemQuantity: BigNumber,
  ): string => {
    const itemWeightedItemQuantity = getWeightedItemQuantity(item, quantity, weightedItemQuantity);
    if (itemWeightedItemQuantity && !itemWeightedItemQuantity.isNaN()) {
      if (isSoldByAverageWeight(item)) {
        return `${item.weightProfile.abbrv} / ea avg`;
      }
      return `${item.weightProfile.abbrv} / ea`;
    }
    return item.weightProfile.abbrv;
  };

  /**
   * Check if an item is an ASC item.
   *
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isAdditionalScanCode = (item: ItemSummary): boolean => {
    return isAdditionalScanCodeUtil(item);
  };

  /**
   * Check if an item is sold by random weight.
   *
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isSoldByRandomWeight = (item: ItemSummary): boolean => {
    return isSoldByRandomWeightUtil(item);
  };

  /**
   * Check if an item is set to display quantity and weight in the weight profile
   *
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isWeightAndQuantityCustomizable = (item: ItemSummary): boolean => {
    return isWeightAndQuantityCustomizableUtil(item);
  };

  /**
   * Check if an item is sold by weight and has a weight profile abbreviation.
   * @param {Object} item The item.
   * @return {boolean}
   */
  const hasWeightAbbreviation = (item: ItemSummary): boolean => {
    return hasWeightAbbreviationUtil(item);
  };

  /**
   * Check if an item is sold by weight.
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isSoldByWeight = (item: ItemSummary): boolean => {
    return isSoldByWeightUtil(item);
  };

  return {
    getOrderedQuantity,
    getWeightedItemQuantity,
    getItemAbbreviation,
    getEachCountFromQuantity,
    isAdditionalScanCode,
    isSoldByRandomWeight,
    isWeightAndQuantityCustomizable,
    isSoldByAverageWeight,
    hasWeightAbbreviation,
    isSoldByWeight,
  };
}
